import { useEffect, useState } from 'react';
import Icon from '../components/Icon';
import Avatar from '../components/Avatar';
import { PRIMARY } from '../components/CaptureSheets';
import { claimAnnouncementSlot } from '../components/Announcements';
import { useAiCredits } from '../lib/credits';
import { useApp } from '../lib/AppContext';
import { avoidLine } from './usePair';

const CARD = 'flex flex-col gap-3 rounded-[20px] bg-base-100 p-5';

function InviteAvatars({ me, partnerName }) {
  return (
    <span className="flex -space-x-3">
      <Avatar name={me?.name || 'Bạn'} url={me?.avatar_url} size={40} />
      <span className={`grid size-10 place-items-center rounded-full border-2 font-bold ${partnerName ? 'border-base-100 bg-[#f6d2dc] text-[#8e2949]' : 'border-dashed border-[#e7a36b] bg-[#fdf0e6] text-[#c2410c]'}`}>
        {partnerName ? partnerName[0] : '+'}
      </span>
    </span>
  );
}

// ⑨ The empty menu tab's second choice, next to "Tạo thực đơn cho tôi". Not an ad, so no ✕.
export function TogetherCard({ onInvite }) {
  return (
    <div className="flex flex-col gap-3 rounded-[20px] bg-[#fdf0e6] p-4">
      <div className="flex items-center gap-3">
        <InviteAvatars />
        <div>
          <p className="font-bold text-[#c2410c]">Ăn cùng ai đó?</p>
          <p className="text-sm text-[#5c3a1e]">Mời trước, rồi tạo một thực đơn cho cả hai. Mỗi người một khẩu phần.</p>
        </div>
      </div>
      <button type="button" className="btn h-12 rounded-[14px] border-0 bg-[#17201b] font-bold text-white" onClick={onInvite}>Mời người nấu chung</button>
    </div>
  );
}

function Step({ state, title, note }) {
  return (
    <li className="flex items-start gap-3">
      <span className={`mt-0.5 grid size-6 shrink-0 place-items-center rounded-full ${state === 'done' ? 'bg-[#17804f] text-white' : state === 'now' ? 'border-2 border-[#e7a33b] bg-[#fdf3d4]' : 'border-2 border-line'}`}>
        {state === 'done' ? <Icon name="check" className="size-3.5" /> : state === 'now' ? <span className="size-2 rounded-full bg-[#e7a33b]" /> : null}
      </span>
      <span>
        <span className={`block text-sm font-semibold ${state === 'next' ? 'text-muted' : ''}`}>{title}</span>
        {note && <span className="block text-xs text-muted">{note}</span>}
      </span>
    </li>
  );
}

const ago = (iso) => {
  const minutes = Math.max(1, Math.round((Date.now() - new Date(iso)) / 60000));
  if (minutes < 60) return `${minutes} phút trước`;
  const hours = Math.round(minutes / 60);
  return hours < 24 ? `${hours} giờ trước` : `${Math.round(hours / 24)} ngày trước`;
};

// ⑪ "Đang chờ": the invite is out and neither has a menu. Resend, cancel, or make your own menu first.
export function WaitingCard({ me, invite, onResend, onCancel, onOwnFirst }) {
  const days = Math.max(0, Math.ceil((new Date(invite.expires_at) - Date.now()) / 86400000));
  return (
    <>
      <section className={CARD}>
        <div className="flex items-center gap-3">
          <InviteAvatars me={me} />
          <div>
            <p className="font-bold">Đang chờ người nấu chung</p>
            <p className="text-xs text-muted">Gửi {ago(invite.created_at)} · hết hạn sau {days} ngày</p>
          </div>
        </div>
        <ol className="flex flex-col gap-3">
          <Step state="done" title="Gửi lời mời" />
          <Step state="now" title="Người được mời nhận lời" note="khai mục tiêu và món không ăn được" />
          <Step state="next" title="Chọn bữa ăn chung" />
          <Step state="next" title="Tạo thực đơn cho 2 người" />
        </ol>
        <div className="grid grid-cols-2 gap-2">
          <button type="button" className="btn h-11 rounded-[14px] border-line bg-base-100 font-semibold" onClick={onResend}>Gửi lại</button>
          <button type="button" className="btn h-11 rounded-[14px] border-line bg-base-100 font-semibold text-error" onClick={onCancel}>Huỷ lời mời</button>
        </div>
      </section>
      <section className={CARD}>
        <p className="font-bold">Không muốn chờ?</p>
        <p className="text-sm text-muted">Tạo thực đơn của bạn trước. Khi người kia nhận lời, các bữa ăn chung sẽ dùng món của bạn và tự chia khẩu phần.</p>
        <button type="button" className="w-fit text-sm font-bold text-brand-text" onClick={onOwnFirst}>Tạo thực đơn của tôi trước</button>
      </section>
    </>
  );
}

// ⑫ "Sẵn sàng": the pair exists and this week has no menu. Either person can make it for both; the tapper
// pays one menu's AI credits (Pro only), and the other phone shows the menu when it loads.
export function ReadyCard({ pair, weekStart, onSlots, onCreated }) {
  const { api, openPaywall } = useApp();
  const credits = useAiCredits(5);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const avoid = avoidLine([pair.me, pair.partner]);
  const create = async () => {
    if (!pair.can_edit) {
      openPaywall('pro_required');
      return;
    }
    if (credits.blocked(create)) return;
    setBusy(true);
    setError(null);
    try {
      const data = await api.post('/cooking_pair/menu', { week_start: weekStart }, { idempotencyKey: credits.key() });
      credits.used();
      onCreated(data.plan);
    } catch (e) {
      setError(e.code === 'menu_in_progress' ? `${pair.partner.name} đang tạo thực đơn, chờ một chút rồi tải lại.` : e.message);
      setBusy(false);
    }
  };
  return (
    <>
      <p className="flex items-center gap-2 rounded-[16px] bg-[#e3f1e9] p-3 text-sm text-[#17201b]">
        <InviteAvatars me={pair.me} partnerName={pair.partner.name} />
        <span><b>{pair.partner.inviter ? `${pair.partner.name} đã mời bạn.` : `${pair.partner.name} đã nhận lời.`}</b> Chỉ còn một bước.</span>
      </p>
      <section className={CARD}>
        <ol className="flex flex-col gap-3">
          <Step state="done" title="Gửi lời mời" />
          <Step state="done" title={`${pair.partner.inviter ? 'Bạn' : pair.partner.name} nhận lời mời`} />
          <Step state="done" title="Chọn bữa ăn chung" note={`${pair.meals_per_week} bữa/tuần`} />
          <Step state="now" title="Tạo thực đơn cho 2 người" />
        </ol>
        <dl className="flex flex-col gap-1.5 rounded-[14px] bg-base-200 p-3 text-xs">
          <div className="flex justify-between gap-3"><dt className="text-muted">Mục tiêu</dt><dd className="font-semibold">Mỗi người theo mục tiêu riêng</dd></div>
          <div className="flex justify-between gap-3"><dt className="text-muted">Không ăn</dt><dd className="text-right font-semibold">{avoid || 'Không có'}</dd></div>
          <div className="flex justify-between gap-3"><dt className="text-muted">Bữa riêng</dt><dd className="font-semibold">Mỗi người tự có món</dd></div>
        </dl>
        <button type="button" className="w-fit text-sm font-semibold text-brand-text" onClick={onSlots}>Đổi bữa ăn chung</button>
        {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
        <button type="button" className={PRIMARY} disabled={busy} onClick={create}>
          {busy ? <><span className="loading loading-spinner loading-sm" /> AI đang lên thực đơn cho 2 người…</> : <><Icon name="plus" className="size-5" /> Tạo thực đơn cho 2 người</>}
        </button>
        <p className="text-center text-xs text-muted">
          {pair.can_edit ? `Dùng lượt AI tạo thực đơn của bạn, tạo cả bữa chung lẫn bữa riêng của hai người. ${pair.partner.name} cũng thấy nút này, ai bấm trước thì dùng lượt người đó.`
            : `Tạo thực đơn cần gói Pro. ${pair.partner.name} cũng có thể tạo cho cả hai.`}
        </p>
      </section>
    </>
  );
}

// ① "Ăn cùng ai đó?" in this or next week's detail, when there's a menu and no pair. ✕ hides it for that week;
// after two ✕ only the ⋯ row stays. It takes the session's one announcement slot.
export function CookBanner({ state, weekStart, onInvite, onDismiss }) {
  const [shown, setShown] = useState(false);
  const banner = state?.banner;
  const eligible = state && !state.pair && banner && banner.dismissals < 2 && !banner.hidden_weeks.includes(weekStart);
  useEffect(() => { if (eligible) setShown(claimAnnouncementSlot('cook_together_v1')); }, [eligible]);
  if (!eligible || !shown) return null;
  return (
    <div className="flex flex-col gap-3 rounded-[20px] bg-[#fdf0e6] p-4">
      <div className="flex items-start gap-3">
        <InviteAvatars />
        <div className="flex-1">
          <p className="font-bold text-[#c2410c]">Ăn cùng ai đó?</p>
          <p className="text-sm text-[#5c3a1e]">Nấu chung thực đơn tuần này, mỗi người một khẩu phần.</p>
        </div>
        <button type="button" aria-label="Ẩn gợi ý nấu chung tuần này" className="grid size-11 place-items-center text-[#c2410c]" onClick={onDismiss}>
          <Icon name="x" className="size-5" />
        </button>
      </div>
      <button type="button" className="btn h-11 rounded-[14px] border-0 bg-[#17201b] font-bold text-white" onClick={onInvite}>
        <Icon name="bowl" className="size-4" /> Mời người nấu chung
      </button>
    </div>
  );
}

// Changes the other person made to this day's shared meals, each with "Hoàn tác".
export function ActivityLines({ lines, onUndo }) {
  const [busy, setBusy] = useState(null);
  if (!lines?.length) return null;
  return (
    <ul className="flex flex-col gap-2">
      {lines.map((line) => (
        <li key={line.id} className="flex items-center gap-3 rounded-[14px] bg-base-100 px-3 py-2 text-sm">
          <Icon name="users" className="size-4 shrink-0 text-[#b2456a]" />
          <span className="flex-1">{line.text}</span>
          {line.can_undo && (
            <button type="button" className="btn btn-sm h-9 rounded-full" disabled={busy === line.id}
              onClick={async () => { setBusy(line.id); try { await onUndo(line); } catch (e) { window.alert(e.message); } finally { setBusy(null); } }}>
              Hoàn tác
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

// "Tạo thực đơn cho bữa riêng": for someone who joined without a menu, whose own meals are still empty.
// A menu's AI credits, Pro only (Free gets the Pro sheet).
export function OwnMealsCard({ planId, canEdit, onDone }) {
  const { api, openPaywall } = useApp();
  const credits = useAiCredits(5);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const create = async () => {
    if (!canEdit) {
      openPaywall('pro_required');
      return;
    }
    if (credits.blocked(create)) return;
    setBusy(true);
    setError(null);
    try {
      onDone(await api.post('/cooking_pair/own_meals', { plan_id: planId }, { idempotencyKey: credits.key() }));
      credits.used();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="flex flex-col gap-2 rounded-[20px] bg-[#fdf3d4] p-4 text-sm text-[#5c4300]">
      <p className="font-bold">Bữa riêng của bạn đang trống</p>
      <p>Bữa ăn chung đã có món. Các bữa còn lại bạn tự ghi, hoặc để AI lên theo mục tiêu của bạn.</p>
      {error && <p role="alert" className="font-medium text-error">{error}</p>}
      <button type="button" className="btn h-11 w-fit rounded-[12px] border-0 bg-[#17201b] px-4 font-bold text-white" disabled={busy} onClick={create}>
        {busy ? <><span className="loading loading-spinner loading-sm" /> AI đang lên bữa riêng…</> : 'Tạo thực đơn cho bữa riêng'}
      </button>
      {canEdit ? <span className="text-xs">{credits.note}</span> : <span className="text-xs">Cần gói Pro</span>}
    </section>
  );
}
